import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of, shareReplay, tap } from 'rxjs';

import { ApiEndpoints } from '../constants/api-endpoints';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import { TokenStorageService } from './token-storage.service';
import { SubscriptionService } from './subscription.service';

export interface AppUserAttachment {
  id?: number;
  accountId?: number;
  attachmentFileName?: string;
  attachmentUrl?: string;
  fileSize?: number;
  status?: number;
}

export interface AppUserProfile {
  id: number;
  accountId?: number;
  renterId?: number;
  name?: string;
  organizationName?: string;
  mobileNumber?: string;
  email?: string;
  username?: string;
  role?: number;
  attachments?: AppUserAttachment[];
}

interface UserListResponse {
  code?: string;
  message?: string;
  totalRows?: number;
  data?: any[];
}

export interface AppUserListResponse {
  code?: string;
  message?: string;
  totalRows: number;
  data: AppUserProfile[];
}

@Injectable({ providedIn: 'root' })
export class AppUserService {
  private readonly baseUrl = environment.apiBaseUrl.replace(/\/+$/, '');
  private readonly currentProfileStorageKey = 'htally_current_profile';
  private readonly currentProfileSubject = new BehaviorSubject<AppUserProfile | null>(this.readCurrentProfileFromStorage());
  private currentProfileRequest$: Observable<AppUserProfile> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly api: ApiService,
    private readonly tokens: TokenStorageService,
    private readonly subscriptionService: SubscriptionService
  ) {}

  me(forceRefresh = false): Observable<AppUserProfile> {
    const cached = this.currentProfileSubject.value;
    if (!forceRefresh && cached) {
      return of(cached);
    }

    if (!forceRefresh && this.currentProfileRequest$) {
      return this.currentProfileRequest$;
    }

    const request$ = this.http.get<any>(`${this.baseUrl}/auth/me`).pipe(
      map((user) => this.normalizeProfile(user)),
      tap((profile) => this.setCurrentProfileCache(profile)),
      shareReplay(1)
    );

    this.currentProfileRequest$ = request$;
    request$.subscribe({
      next: () => {
        this.currentProfileRequest$ = null;
      },
      error: () => {
        this.currentProfileRequest$ = null;
      }
    });

    return request$;
  }

  basicInfoFromList(): Observable<AppUserProfile | null> {
    const cached = this.currentProfileSubject.value;
    if (cached) {
      return of(cached);
    }

    const currentSubject = this.getTokenSubject();
    return this.listProfiles().pipe(
      map((res) => {
        const users = Array.isArray(res) ? res : [];
        if (!users.length) {
          return null;
        }

        if (currentSubject) {
          const matched = users.find((u) => {
            const username = `${u?.username ?? ''}`.trim().toLowerCase();
            const email = `${u?.email ?? ''}`.trim().toLowerCase();
            const sub = currentSubject.toLowerCase();
            return username === sub || email === sub;
          });
          if (matched) {
            this.setCurrentProfileCache(matched);
            return matched;
          }
        }

        const fallback = users[0];
        this.setCurrentProfileCache(fallback);
        return fallback;
      })
    );
  }

  findById(id: number): Observable<AppUserProfile | null> {
    const targetId = Number(id);
    if (!Number.isFinite(targetId)) {
      return of(null);
    }

    return this.listProfiles({ id: targetId }, 1).pipe(
      map((users) => users[0] ?? null)
    );
  }

  resolveAccountOwnerProfile(profile: AppUserProfile | null | undefined): Observable<AppUserProfile | null> {
    const currentProfile = profile ?? null;
    const currentId = Number(currentProfile?.id);
    const accountId = Number(currentProfile?.accountId);

    if (!Number.isFinite(accountId) || accountId <= 0 || accountId === currentId) {
      return of(currentProfile);
    }

    return this.findById(accountId).pipe(
      map((owner) => owner ?? currentProfile)
    );
  }

  listUsers(payload: {
    filters?: Record<string, any>;
    pagination?: boolean;
    pageNo?: number;
    rowCount?: number;
  } = {}): Observable<AppUserListResponse> {
    return this.api.postHandler(ApiEndpoints.APP_USER_LIST, {
      pagination: payload.pagination ?? true,
      pageNo: payload.pageNo ?? 0,
      rowCount: payload.rowCount ?? 20,
      filters: payload.filters ?? {}
    }).pipe(
      map((res) => ({
        code: res?.code,
        message: res?.message,
        totalRows: Number(res?.totalRows ?? 0),
        data: (Array.isArray(res?.data) ? res.data : []).map((user: any) => this.normalizeProfile(user))
      }))
    );
  }

  listEmployees(payload: {
    filters?: Record<string, any>;
    pagination?: boolean;
    pageNo?: number;
    rowCount?: number;
  } = {}): Observable<AppUserListResponse> {
    return this.listUsers({
      ...payload,
      filters: {
        ...(payload.filters ?? {}),
        role: 3
      }
    });
  }

  createEmployee(payload: {
    name: string;
    mobileNumber: string;
    email?: string;
  }): Observable<any> {
    return this.api.postHandler(ApiEndpoints.APP_USER_EMPLOYEE_CREATE, {
      name: payload.name,
      mobileNumber: payload.mobileNumber,
      email: payload.email
    }).pipe(
      tap((res: any) => {
        if (`${res?.code ?? ''}` === '000') {
          this.subscriptionService.requestSummaryRefresh();
        }
      })
    );
  }

  updateEmployee(payload: {
    id: number;
    name: string;
    mobileNumber: string;
    email?: string;
  }): Observable<any> {
    return this.api.postHandler(ApiEndpoints.APP_USER_EMPLOYEE_UPDATE, {
      id: payload.id,
      name: payload.name,
      mobileNumber: payload.mobileNumber,
      email: payload.email
    });
  }

  deleteEmployee(id: number): Observable<any> {
    return this.api.postHandler(ApiEndpoints.APP_USER_EMPLOYEE_DELETE, {
      id
    });
  }

  private normalizeProfile(raw: any): AppUserProfile {
    const normalized: AppUserProfile = {
      ...(raw ?? {}),
      organizationName:
        `${raw?.organizationName ?? raw?.organization_name ?? ''}`.trim() || undefined
    };
    return normalized;
  }

  updateBasicProfile(payload: {
    id?: number;
    name?: string;
    organizationName?: string;
    mobileNumber?: string;
  }): Observable<any> {
    return this.api.postHandler(ApiEndpoints.APP_USER_PROFILE_BASIC_UPDATE, {
      id: payload.id,
      name: payload.name,
      organizationName: payload.organizationName,
      mobileNumber: payload.mobileNumber
    });
  }

  clearCurrentProfileCache(): void {
    this.currentProfileRequest$ = null;
    this.currentProfileSubject.next(null);
    const storage = this.getStorage();
    storage?.removeItem(this.currentProfileStorageKey);
  }

  private listProfiles(filters: Record<string, any> = {}, rowCount = 200): Observable<AppUserProfile[]> {
    return this.api.postHandler(ApiEndpoints.APP_USER_LIST, {
      pagination: true,
      pageNo: 0,
      rowCount,
      filters
    }).pipe(
      map((res) => (Array.isArray(res?.data) ? res.data : []).map((user: any) => this.normalizeProfile(user)))
    );
  }

  requestLegacyProfileOtp(email: string): Observable<any> {
    return this.api.postHandler(ApiEndpoints.APP_USER_PROFILE_OTP, {
      email
    });
  }

  sendOldEmailChangeOtp(newEmail: string): Observable<any> {
    return this.api.postHandler(ApiEndpoints.APP_USER_PROFILE_EMAIL_OLD_OTP, {
      newEmail
    });
  }

  sendNewEmailChangeOtp(newEmail: string, oldEmailOtp: string, id?: number): Observable<any> {
    return this.api.postHandler(ApiEndpoints.APP_USER_PROFILE_EMAIL_NEW_OTP, {
      id,
      newEmail,
      oldEmailOtp
    });
  }

  updateProfileEmail(newEmail: string, newEmailOtp: string, id?: number): Observable<any> {
    return this.api.postHandler(ApiEndpoints.APP_USER_PROFILE_EMAIL_UPDATE, {
      id,
      newEmail,
      newEmailOtp
    });
  }

  updatePassword(currentPassword: string, newPassword: string, id?: number): Observable<any> {
    return this.api.postHandler(ApiEndpoints.APP_USER_PROFILE_PASSWORD_UPDATE, {
      id,
      currentPassword,
      newPassword
    });
  }

  private getTokenSubject(): string | null {
    const token = this.tokens.accessToken;
    if (!token) {
      return null;
    }

    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(parts[1]));
      const subject = `${payload?.sub ?? ''}`.trim();
      return subject || null;
    } catch {
      return null;
    }
  }

  private setCurrentProfileCache(profile: AppUserProfile | null): void {
    this.currentProfileSubject.next(profile);
    const storage = this.getStorage();
    if (!storage) {
      return;
    }
    if (!profile) {
      storage.removeItem(this.currentProfileStorageKey);
      return;
    }
    storage.setItem(this.currentProfileStorageKey, JSON.stringify(profile));
  }

  private readCurrentProfileFromStorage(): AppUserProfile | null {
    const storage = this.getStorage();
    const raw = `${storage?.getItem(this.currentProfileStorageKey) ?? ''}`.trim();
    if (!raw) {
      return null;
    }
    try {
      return this.normalizeProfile(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  private getStorage(): Storage | null {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  }
}
